import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  LogOut, Save, Plus, Trash2, Edit2, X, Check, 
  Utensils, Beer, Calendar, Phone, ChevronDown, ChevronUp, AlertCircle,
  FileText, Image, Upload, Bell, Eye, EyeOff, Archive, RotateCcw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const isDev = process.env.NODE_ENV === 'development';

// Safe logging helper
const logError = (message, error) => {
  if (isDev) {
    console.error(message, error);
  }
};

// Auth helper
const getAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-[#2C1810] text-white hover:bg-[#3d221a] transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon size={24} className="text-[#C17C3A]" />
          <h2 className="font-playfair text-xl font-semibold">{title}</h2>
        </div>
        {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </button>
      {isOpen && <div className="p-6">{children}</div>}
    </div>
  );
};

// Menu Management Component
const MenuManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ category: "starters", name: "", price: "", description: "" });
  const [showAddForm, setShowAddForm] = useState(false);

  const categories = [
    { value: "starters", label: "Starters" },
    { value: "mains", label: "Main Courses" },
    { value: "vegetarian", label: "Vegetarian & Vegan" },
    { value: "grills", label: "Grills" },
    { value: "salads", label: "Salads" },
    { value: "sides", label: "Sides" },
    { value: "childrens", label: "Children's Menu" },
    { value: "steakSauces", label: "Steak Sauces" },
    { value: "desserts", label: "Desserts" },
  ];

  const fetchItems = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/menu`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      logError("Error fetching menu:", err);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const addItem = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/menu`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify(newItem),
      });
      if (res.ok) {
        fetchItems();
        setNewItem({ category: "starters", name: "", price: "", description: "" });
        setShowAddForm(false);
      }
    } catch (err) {
      logError("Error adding item:", err);
    }
  };

  const updateItem = async (item) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/menu/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify(item),
      });
      if (res.ok) {
        fetchItems();
        setEditingItem(null);
      }
    } catch (err) {
      logError("Error updating item:", err);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/menu/${id}`, {
        method: "DELETE",
        headers: getAuthHeader(),
      });
      if (res.ok) fetchItems();
    } catch (err) {
      logError("Error deleting item:", err);
    }
  };

  const groupedItems = categories.map(cat => ({
    ...cat,
    items: items.filter(item => item.category === cat.value)
  }));

  return (
    <div>
      <button
        onClick={() => setShowAddForm(!showAddForm)}
        className="mb-4 flex items-center gap-2 bg-[#C17C3A] text-white px-4 py-2 rounded-lg hover:bg-[#A0652D] transition-colors"
      >
        <Plus size={18} /> Add Menu Item
      </button>

      {showAddForm && (
        <div className="mb-6 p-4 bg-[#F5F1E6] rounded-lg">
          <h3 className="font-semibold mb-3">Add New Item</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <select
              value={newItem.category}
              onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Name"
              value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            />
            <input
              type="text"
              placeholder="Price (e.g., £11.50)"
              value={newItem.price}
              onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            />
            <input
              type="text"
              placeholder="Description"
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            />
          </div>
          <button
            onClick={addItem}
            className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            Save Item
          </button>
        </div>
      )}

      {groupedItems.map(group => (
        group.items.length > 0 && (
          <div key={group.value} className="mb-6">
            <h3 className="font-playfair text-lg font-semibold text-[#2C1810] mb-2 border-b border-[#C17C3A] pb-1">
              {group.label}
            </h3>
            <div className="space-y-2">
              {group.items.map(item => (
                <div key={item.id} className="flex items-center gap-3 p-3 bg-[#F5F1E6] rounded-lg">
                  {editingItem?.id === item.id ? (
                    <>
                      <input
                        type="text"
                        value={editingItem.name}
                        onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                        className="flex-1 px-2 py-1 border rounded"
                      />
                      <input
                        type="text"
                        value={editingItem.price}
                        onChange={(e) => setEditingItem({ ...editingItem, price: e.target.value })}
                        className="w-24 px-2 py-1 border rounded"
                      />
                      <input
                        type="text"
                        value={editingItem.description}
                        onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                        className="flex-1 px-2 py-1 border rounded"
                      />
                      <button onClick={() => updateItem(editingItem)} className="text-green-600 hover:text-green-800">
                        <Check size={18} />
                      </button>
                      <button onClick={() => setEditingItem(null)} className="text-gray-600 hover:text-gray-800">
                        <X size={18} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 font-medium">{item.name}</span>
                      <span className="text-[#C17C3A] font-semibold w-24">{item.price}</span>
                      <span className="flex-1 text-gray-600 text-sm">{item.description}</span>
                      <button onClick={() => setEditingItem(item)} className="text-blue-600 hover:text-blue-800">
                        <Edit2 size={18} />
                      </button>
                      <button onClick={() => deleteItem(item.id)} className="text-red-600 hover:text-red-800">
                        <Trash2 size={18} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      ))}

      {items.length === 0 && (
        <p className="text-gray-500 italic">No menu items yet. Add your first item above!</p>
      )}
    </div>
  );
};

// Drinks Management Component
const DrinksManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ category: "lagers", name: "", price: "" });
  const [showAddForm, setShowAddForm] = useState(false);

  const categories = [
    { value: "lagers", label: "Lagers" },
    { value: "ciders", label: "Ciders" },
    { value: "ales", label: "Ales & Stouts" },
    { value: "bottledBeers", label: "Bottled Beers" },
    { value: "bottledCiders", label: "Bottled Ciders" },
    { value: "wines", label: "Wines" },
    { value: "spirits", label: "Spirits" },
    { value: "softDrinks", label: "Soft Drinks" },
  ];

  const fetchItems = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/drinks`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      logError("Error fetching drinks:", err);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const addItem = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/drinks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify(newItem),
      });
      if (res.ok) {
        fetchItems();
        setNewItem({ category: "lagers", name: "", price: "" });
        setShowAddForm(false);
      }
    } catch (err) {
      logError("Error adding drink:", err);
    }
  };

  const updateItem = async (item) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/drinks/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify(item),
      });
      if (res.ok) {
        fetchItems();
        setEditingItem(null);
      }
    } catch (err) {
      logError("Error updating drink:", err);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Are you sure you want to delete this drink?")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/drinks/${id}`, {
        method: "DELETE",
        headers: getAuthHeader(),
      });
      if (res.ok) fetchItems();
    } catch (err) {
      logError("Error deleting drink:", err);
    }
  };

  const groupedItems = categories.map(cat => ({
    ...cat,
    items: items.filter(item => item.category === cat.value)
  }));

  return (
    <div>
      <button
        onClick={() => setShowAddForm(!showAddForm)}
        className="mb-4 flex items-center gap-2 bg-[#C17C3A] text-white px-4 py-2 rounded-lg hover:bg-[#A0652D] transition-colors"
      >
        <Plus size={18} /> Add Drink
      </button>

      {showAddForm && (
        <div className="mb-6 p-4 bg-[#F5F1E6] rounded-lg">
          <h3 className="font-semibold mb-3">Add New Drink</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={newItem.category}
              onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Name"
              value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            />
            <input
              type="text"
              placeholder="Price (e.g., £5.20 / £2.60)"
              value={newItem.price}
              onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            />
          </div>
          <button
            onClick={addItem}
            className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            Save Drink
          </button>
        </div>
      )}

      {groupedItems.map(group => (
        group.items.length > 0 && (
          <div key={group.value} className="mb-6">
            <h3 className="font-playfair text-lg font-semibold text-[#2C1810] mb-2 border-b border-[#C17C3A] pb-1">
              {group.label}
            </h3>
            <div className="space-y-2">
              {group.items.map(item => (
                <div key={item.id} className="flex items-center gap-3 p-3 bg-[#F5F1E6] rounded-lg">
                  {editingItem?.id === item.id ? (
                    <>
                      <input
                        type="text"
                        value={editingItem.name}
                        onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                        className="flex-1 px-2 py-1 border rounded"
                      />
                      <input
                        type="text"
                        value={editingItem.price}
                        onChange={(e) => setEditingItem({ ...editingItem, price: e.target.value })}
                        className="w-32 px-2 py-1 border rounded"
                      />
                      <button onClick={() => updateItem(editingItem)} className="text-green-600 hover:text-green-800">
                        <Check size={18} />
                      </button>
                      <button onClick={() => setEditingItem(null)} className="text-gray-600 hover:text-gray-800">
                        <X size={18} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 font-medium">{item.name}</span>
                      <span className="text-[#C17C3A] font-semibold">{item.price}</span>
                      <button onClick={() => setEditingItem(item)} className="text-blue-600 hover:text-blue-800">
                        <Edit2 size={18} />
                      </button>
                      <button onClick={() => deleteItem(item.id)} className="text-red-600 hover:text-red-800">
                        <Trash2 size={18} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      ))}

      {items.length === 0 && (
        <p className="text-gray-500 italic">No drinks yet. Add your first drink above!</p>
      )}
    </div>
  );
};

// Events (What's On) Management Component
const EventsManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ day: "Monday", title: "", time: "", description: "" });
  const [showAddForm, setShowAddForm] = useState(false);

  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const fetchItems = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/events`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      logError("Error fetching events:", err);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const addItem = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify(newItem),
      });
      if (res.ok) {
        fetchItems();
        setNewItem({ day: "Monday", title: "", time: "", description: "" });
        setShowAddForm(false);
      }
    } catch (err) {
      logError("Error adding event:", err);
    }
  };

  const updateItem = async (item) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/events/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify(item),
      });
      if (res.ok) {
        fetchItems();
        setEditingItem(null);
      }
    } catch (err) {
      logError("Error updating event:", err);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/events/${id}`, {
        method: "DELETE",
        headers: getAuthHeader(),
      });
      if (res.ok) fetchItems();
    } catch (err) {
      logError("Error deleting event:", err);
    }
  };

  return (
    <div>
      <button
        onClick={() => setShowAddForm(!showAddForm)}
        className="mb-4 flex items-center gap-2 bg-[#C17C3A] text-white px-4 py-2 rounded-lg hover:bg-[#A0652D] transition-colors"
      >
        <Plus size={18} /> Add Event
      </button>

      {showAddForm && (
        <div className="mb-6 p-4 bg-[#F5F1E6] rounded-lg">
          <h3 className="font-semibold mb-3">Add New Event</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <select
              value={newItem.day}
              onChange={(e) => setNewItem({ ...newItem, day: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            >
              {days.map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Title (e.g., Pub Quiz)"
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            />
            <input
              type="text"
              placeholder="Time (e.g., 9:00 PM)"
              value={newItem.time}
              onChange={(e) => setNewItem({ ...newItem, time: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            />
            <input
              type="text"
              placeholder="Description"
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            />
          </div>
          <button
            onClick={addItem}
            className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            Save Event
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {days.map(day => {
          const dayEvents = items.filter(item => item.day === day);
          return (
            <div key={day} className="bg-[#F5F1E6] rounded-lg p-3">
              <h4 className="font-semibold text-[#2C1810] border-b border-[#C17C3A]/30 pb-1 mb-2">{day}</h4>
              {dayEvents.length > 0 ? (
                <div className="space-y-2">
                  {dayEvents.map(item => (
                    <div key={item.id} className="text-sm">
                      {editingItem?.id === item.id ? (
                        <div className="space-y-1">
                          <input
                            type="text"
                            value={editingItem.title}
                            onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                            className="w-full px-1 py-0.5 border rounded text-xs"
                          />
                          <input
                            type="text"
                            value={editingItem.time}
                            onChange={(e) => setEditingItem({ ...editingItem, time: e.target.value })}
                            className="w-full px-1 py-0.5 border rounded text-xs"
                          />
                          <input
                            type="text"
                            value={editingItem.description}
                            onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                            className="w-full px-1 py-0.5 border rounded text-xs"
                          />
                          <div className="flex gap-1">
                            <button onClick={() => updateItem(editingItem)} className="text-green-600">
                              <Check size={14} />
                            </button>
                            <button onClick={() => setEditingItem(null)} className="text-gray-600">
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="font-medium text-[#2C1810]">{item.title}</p>
                          {item.time && <p className="text-[#C17C3A] text-xs">{item.time}</p>}
                          <p className="text-gray-600 text-xs">{item.description}</p>
                          <div className="flex gap-1 mt-1">
                            <button onClick={() => setEditingItem(item)} className="text-blue-600">
                              <Edit2 size={14} />
                            </button>
                            <button onClick={() => deleteItem(item.id)} className="text-red-600">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-xs italic">No events</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Contact Info Management Component
const ContactManager = ({ token }) => {
  const [info, setInfo] = useState({
    phone: "",
    email: "",
    booking_notification_email: "the.threehorseshoes@live.co.uk",
    resend_api_key: "",
    monday_thursday_hours: "",
    friday_saturday_hours: "",
    sunday_hours: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchInfo = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact-info`);
      const data = await res.json();
      setInfo({
        phone: data.phone || "",
        email: data.email || "",
        booking_notification_email: data.booking_notification_email || "the.threehorseshoes@live.co.uk",
        resend_api_key: data.resend_api_key || "",
        monday_thursday_hours: data.monday_thursday_hours || "",
        friday_saturday_hours: data.friday_saturday_hours || "",
        sunday_hours: data.sunday_hours || "",
      });
    } catch (err) {
      logError("Error fetching contact info:", err);
    }
  }, []);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  const saveInfo = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/contact-info`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify(info),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      logError("Error saving contact info:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="font-semibold text-[#2C1810]">Public Contact Details (shown on website)</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
          <input
            type="text"
            value={info.phone}
            onChange={(e) => setInfo({ ...info, phone: e.target.value })}
            placeholder="e.g., 01234 567890"
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address (public)</label>
          <input
            type="email"
            value={info.email}
            onChange={(e) => setInfo({ ...info, email: e.target.value })}
            placeholder="e.g., info@threehorseshoes.com"
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>

      <h3 className="font-semibold text-[#2C1810] mt-4">Opening Hours</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Monday - Thursday</label>
          <input
            type="text"
            value={info.monday_thursday_hours}
            onChange={(e) => setInfo({ ...info, monday_thursday_hours: e.target.value })}
            placeholder="e.g., 12:00 - 11:00 PM"
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Friday - Saturday</label>
          <input
            type="text"
            value={info.friday_saturday_hours}
            onChange={(e) => setInfo({ ...info, friday_saturday_hours: e.target.value })}
            placeholder="e.g., 12:00 - 12:00 AM"
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sunday</label>
          <input
            type="text"
            value={info.sunday_hours}
            onChange={(e) => setInfo({ ...info, sunday_hours: e.target.value })}
            placeholder="e.g., 12:00 - 10:30 PM"
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>

      <button
        onClick={saveInfo}
        disabled={saving}
        className="flex items-center gap-2 bg-[#C17C3A] text-white px-6 py-2 rounded-lg hover:bg-[#A0652D] transition-colors disabled:opacity-50"
      >
        {saving ? "Saving..." : saved ? <><Check size={18} /> Saved!</> : <><Save size={18} /> Save Contact Info</>}
      </button>
    </div>
  );
};

// Bookings Viewer Component - Now with Accept/Decline for Inquiries
const BookingsViewer = ({ token }) => {
  const [bookings, setBookings] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [declineModal, setDeclineModal] = useState(null); // { bookingRef, includeReason: false, reason: '' }
  const [processing, setProcessing] = useState(null); // booking_ref being processed

  const fetchBookings = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/bookings?include_archived=${showArchived}`, {
        headers: getAuthHeader(),
      });
      const data = await res.json();
      setBookings(data.bookings || []);
    } catch (err) {
      logError("Error fetching bookings:", err);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const acceptBooking = async (bookingRef) => {
    if (!window.confirm("Accept this booking? A confirmation email will be sent to the customer.")) return;
    setProcessing(bookingRef);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/bookings/${bookingRef}/accept`, {
        method: "PUT",
        headers: getAuthHeader(),
      });
      if (res.ok) {
        const data = await res.json();
        alert(data.email_sent ? "Booking accepted! Confirmation email sent to customer." : "Booking accepted! (Email not configured)");
        fetchBookings();
      } else {
        const error = await res.json();
        alert(error.detail || "Failed to accept booking");
      }
    } catch (err) {
      logError("Error accepting booking:", err);
      alert("Failed to accept booking");
    } finally {
      setProcessing(null);
    }
  };

  const declineBooking = async () => {
    if (!declineModal) return;
    setProcessing(declineModal.bookingRef);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/bookings/${declineModal.bookingRef}/decline`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({
          include_reason: declineModal.includeReason,
          reason: declineModal.reason
        }),
      });
      if (res.ok) {
        const data = await res.json();
        alert(data.email_sent ? "Booking declined. Apology email sent to customer." : "Booking declined. (Email not configured)");
        fetchBookings();
      } else {
        const error = await res.json();
        alert(error.detail || "Failed to decline booking");
      }
    } catch (err) {
      logError("Error declining booking:", err);
      alert("Failed to decline booking");
    } finally {
      setProcessing(null);
      setDeclineModal(null);
    }
  };

  const archiveBooking = async (bookingRef) => {
    if (!window.confirm("Archive this booking? It will be hidden from the main list.")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/bookings/${bookingRef}/archive`, {
        method: "PUT",
        headers: getAuthHeader(),
      });
      if (res.ok) fetchBookings();
    } catch (err) {
      logError("Error archiving booking:", err);
    }
  };

  const unarchiveBooking = async (bookingRef) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/bookings/${bookingRef}/unarchive`, {
        method: "PUT",
        headers: getAuthHeader(),
      });
      if (res.ok) fetchBookings();
    } catch (err) {
      logError("Error unarchiving booking:", err);
    }
  };

  const deleteBooking = async (bookingRef) => {
    if (!window.confirm("Permanently delete this booking? This cannot be undone.")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/bookings/${bookingRef}`, {
        method: "DELETE",
        headers: getAuthHeader(),
      });
      if (res.ok) fetchBookings();
    } catch (err) {
      logError("Error deleting booking:", err);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded">Pending</span>;
      case 'accepted':
        return <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">Confirmed</span>;
      case 'declined':
        return <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">Declined</span>;
      default:
        return <span className="text-xs bg-gray-500 text-white px-2 py-0.5 rounded">Unknown</span>;
    }
  };

  const pendingBookings = bookings.filter(b => b.status === 'pending' && !b.archived);
  const acceptedBookings = bookings.filter(b => b.status === 'accepted' && !b.archived);
  const declinedBookings = bookings.filter(b => b.status === 'declined' && !b.archived);
  const archivedBookings = bookings.filter(b => b.archived);

  return (
    <div>
      {/* Decline Modal */}
      {declineModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="font-playfair text-xl font-semibold text-[#2C1810] mb-4">Decline Booking</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to decline this booking? An email will be sent to the customer.
            </p>
            
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={declineModal.includeReason}
                  onChange={(e) => setDeclineModal({ ...declineModal, includeReason: e.target.checked })}
                  className="w-4 h-4 text-[#C17C3A] rounded"
                />
                <span className="text-sm text-gray-700">Include a custom reason</span>
              </label>
            </div>
            
            {declineModal.includeReason && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (will be shown to customer)</label>
                <textarea
                  value={declineModal.reason}
                  onChange={(e) => setDeclineModal({ ...declineModal, reason: e.target.value })}
                  placeholder="e.g., We're fully booked at this time, We have a private event..."
                  className="w-full px-3 py-2 border rounded-lg resize-none h-24"
                />
              </div>
            )}
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeclineModal(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={declineBooking}
                disabled={processing}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {processing ? "Declining..." : "Decline Booking"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <p className="text-sm text-gray-600">
          <span className="font-semibold text-amber-600">{pendingBookings.length}</span> pending • 
          <span className="font-semibold text-green-600 ml-1">{acceptedBookings.length}</span> confirmed • 
          <span className="font-semibold text-red-600 ml-1">{declinedBookings.length}</span> declined
          {showArchived && archivedBookings.length > 0 && <span className="ml-1">• {archivedBookings.length} archived</span>}
        </p>
        <button
          onClick={() => setShowArchived(!showArchived)}
          className="flex items-center gap-2 text-sm text-[#2C1810] hover:text-[#C17C3A] transition-colors ml-auto"
        >
          {showArchived ? <EyeOff size={16} /> : <Eye size={16} />}
          {showArchived ? "Hide Archived" : "Show Archived"}
        </button>
      </div>

      {/* Pending Bookings Section */}
      {pendingBookings.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
            <AlertCircle size={18} />
            Pending Inquiries ({pendingBookings.length})
          </h3>
          <div className="space-y-4">
            {pendingBookings.map((booking, index) => (
              <BookingCard 
                key={booking.booking_ref || index} 
                booking={booking} 
                onAccept={acceptBooking}
                onDecline={(ref) => setDeclineModal({ bookingRef: ref, includeReason: false, reason: '' })}
                onArchive={archiveBooking}
                onUnarchive={unarchiveBooking}
                onDelete={deleteBooking}
                expandedBooking={expandedBooking}
                setExpandedBooking={setExpandedBooking}
                getStatusBadge={getStatusBadge}
                processing={processing}
              />
            ))}
          </div>
        </div>
      )}

      {/* Confirmed Bookings Section */}
      {acceptedBookings.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
            <Check size={18} />
            Confirmed Bookings ({acceptedBookings.length})
          </h3>
          <div className="space-y-4">
            {acceptedBookings.map((booking, index) => (
              <BookingCard 
                key={booking.booking_ref || index} 
                booking={booking} 
                onArchive={archiveBooking}
                onUnarchive={unarchiveBooking}
                onDelete={deleteBooking}
                expandedBooking={expandedBooking}
                setExpandedBooking={setExpandedBooking}
                getStatusBadge={getStatusBadge}
                processing={processing}
              />
            ))}
          </div>
        </div>
      )}

      {/* Declined Bookings Section */}
      {declinedBookings.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
            <X size={18} />
            Declined Bookings ({declinedBookings.length})
          </h3>
          <div className="space-y-4">
            {declinedBookings.map((booking, index) => (
              <BookingCard 
                key={booking.booking_ref || index} 
                booking={booking} 
                onArchive={archiveBooking}
                onUnarchive={unarchiveBooking}
                onDelete={deleteBooking}
                expandedBooking={expandedBooking}
                setExpandedBooking={setExpandedBooking}
                getStatusBadge={getStatusBadge}
                processing={processing}
              />
            ))}
          </div>
        </div>
      )}

      {/* Archived Bookings Section */}
      {showArchived && archivedBookings.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold text-gray-500 mb-3 flex items-center gap-2">
            <Archive size={18} />
            Archived ({archivedBookings.length})
          </h3>
          <div className="space-y-4 opacity-75">
            {archivedBookings.map((booking, index) => (
              <BookingCard 
                key={booking.booking_ref || index} 
                booking={booking} 
                onArchive={archiveBooking}
                onUnarchive={unarchiveBooking}
                onDelete={deleteBooking}
                expandedBooking={expandedBooking}
                setExpandedBooking={setExpandedBooking}
                getStatusBadge={getStatusBadge}
                processing={processing}
              />
            ))}
          </div>
        </div>
      )}

      {bookings.length === 0 && (
        <p className="text-gray-500 italic">No booking inquiries yet.</p>
      )}
    </div>
  );
};

// Individual Booking Card Component
const BookingCard = ({ 
  booking, 
  onAccept, 
  onDecline, 
  onArchive, 
  onUnarchive, 
  onDelete, 
  expandedBooking, 
  setExpandedBooking,
  getStatusBadge,
  processing
}) => {
  const isPending = booking.status === 'pending';
  const isProcessing = processing === booking.booking_ref;

  return (
    <div className={`rounded-lg p-4 ${booking.archived ? 'bg-gray-100' : isPending ? 'bg-amber-50 border-2 border-amber-300' : 'bg-[#F5F1E6]'}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-[#C17C3A]">{booking.booking_ref}</span>
          {getStatusBadge(booking.status)}
          {booking.archived && (
            <span className="text-xs bg-gray-500 text-white px-2 py-0.5 rounded">Archived</span>
          )}
          <span className="text-gray-500 text-sm">
            {new Date(booking.created_at).toLocaleDateString('en-GB')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm bg-[#2C1810] text-white px-2 py-1 rounded">
            {booking.party_size} guests
          </span>
        </div>
      </div>
      
      <p className="font-medium">{booking.contact_name}</p>
      <p className="text-sm text-gray-600">{booking.contact_email} • {booking.contact_phone}</p>
      <p className="text-sm mt-1">
        <span className="font-medium">Date:</span> {booking.booking_date} at {booking.booking_time}
      </p>
      {booking.special_requests && (
        <p className="text-sm text-gray-600 mt-1">
          <span className="font-medium">Notes:</span> {booking.special_requests}
        </p>
      )}
      {booking.decline_reason && (
        <p className="text-sm text-red-600 mt-1">
          <span className="font-medium">Decline reason:</span> {booking.decline_reason}
        </p>
      )}
      
      {/* Accept/Decline buttons for pending bookings */}
      {isPending && !booking.archived && (
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onAccept(booking.booking_ref)}
            disabled={isProcessing}
            className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {isProcessing ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Check size={16} />
            )}
            Accept
          </button>
          <button
            onClick={() => onDecline(booking.booking_ref)}
            disabled={isProcessing}
            className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <X size={16} />
            Decline
          </button>
        </div>
      )}
      
      {/* Archive/Delete actions */}
      <div className="flex gap-1 mt-3 pt-3 border-t border-gray-200">
        {booking.archived ? (
          <button
            onClick={() => onUnarchive(booking.booking_ref)}
            className="flex items-center gap-1 text-sm text-green-600 hover:text-green-800"
          >
            <RotateCcw size={14} />
            Restore
          </button>
        ) : (
          <button
            onClick={() => onArchive(booking.booking_ref)}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
          >
            <Archive size={14} />
            Archive
          </button>
        )}
        <button
          onClick={() => onDelete(booking.booking_ref)}
          className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 ml-auto"
        >
          <Trash2 size={14} />
          Delete
        </button>
      </div>

      {/* Expandable pre-order details */}
      {booking.guests && booking.guests.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setExpandedBooking(expandedBooking === booking.booking_ref ? null : booking.booking_ref)}
            className="text-sm text-[#C17C3A] hover:underline flex items-center gap-1"
          >
            {expandedBooking === booking.booking_ref ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expandedBooking === booking.booking_ref ? "Hide" : "View"} Pre-order Details
          </button>
          
          {expandedBooking === booking.booking_ref && (
            <div className="mt-2 p-3 bg-white rounded border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1">Guest</th>
                    <th className="text-left py-1">Starter</th>
                    <th className="text-left py-1">Main</th>
                    <th className="text-left py-1">Dessert</th>
                    <th className="text-left py-1">Dietary Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {booking.guests.map((guest, gIdx) => (
                    <tr key={gIdx} className="border-b last:border-0">
                      <td className="py-1 font-medium">{guest.guest_name}</td>
                      <td className="py-1">{guest.starter || '-'}</td>
                      <td className="py-1">{guest.main || '-'}</td>
                      <td className="py-1">{guest.dessert || '-'}</td>
                      <td className="py-1 text-gray-500">{guest.dietary_notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Closure Status Manager Component
const ClosureManager = ({ token }) => {
  const [closureStatus, setClosureStatus] = useState({
    kitchen_closed: false,
    pub_closed: false,
    closure_date: "",
    closure_reason: ""
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/closure-status`);
      const data = await res.json();
      setClosureStatus({
        kitchen_closed: data.kitchen_closed || false,
        pub_closed: data.pub_closed || false,
        closure_date: data.closure_date || "",
        closure_reason: data.closure_reason || ""
      });
    } catch (err) {
      logError("Error fetching closure status:", err);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const saveStatus = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/closure-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({
          ...closureStatus,
          closure_date: closureStatus.closure_date || null
        }),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      logError("Error saving closure status:", err);
    } finally {
      setSaving(false);
    }
  };

  const clearClosure = () => {
    setClosureStatus({
      kitchen_closed: false,
      pub_closed: false,
      closure_date: "",
      closure_reason: ""
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Indefinite (until manually cleared)";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const isActive = closureStatus.kitchen_closed || closureStatus.pub_closed;

  return (
    <div className="space-y-6">
      {/* Current Status Display */}
      {isActive && (
        <div className="p-4 bg-red-100 border border-red-300 rounded-lg">
          <h3 className="font-semibold text-red-800 mb-2">⚠️ Active Closure</h3>
          <p className="text-red-700">
            {closureStatus.pub_closed ? "Pub is closed" : "Kitchen is closed"}
            {" "}on <strong>{formatDate(closureStatus.closure_date)}</strong>
          </p>
          {closureStatus.closure_reason && (
            <p className="text-red-600 text-sm mt-1">Reason: {closureStatus.closure_reason}</p>
          )}
          <button
            onClick={clearClosure}
            className="mt-3 text-sm text-red-600 underline hover:text-red-800"
          >
            Clear closure
          </button>
        </div>
      )}

      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="font-semibold text-[#2C1810] mb-4">Set Closure</h3>
        <p className="text-sm text-gray-600 mb-4">
          Close the kitchen or pub for a specific date. Leave the date empty to close indefinitely until manually cleared.
        </p>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">What to close</label>
            <select
              value={closureStatus.pub_closed ? "pub" : closureStatus.kitchen_closed ? "kitchen" : ""}
              onChange={(e) => {
                const val = e.target.value;
                setClosureStatus({
                  ...closureStatus,
                  pub_closed: val === "pub",
                  kitchen_closed: val === "kitchen" || val === "pub"
                });
              }}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">Not closed</option>
              <option value="kitchen">Kitchen only (no food)</option>
              <option value="pub">Entire pub</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Closure date (optional)</label>
            <input
              type="date"
              value={closureStatus.closure_date}
              onChange={(e) => setClosureStatus({ ...closureStatus, closure_date: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Leave empty for indefinite"
            />
            <p className="text-xs text-gray-500 mt-1">Leave empty to close until manually cleared</p>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason (shown to customers)</label>
          <input
            type="text"
            value={closureStatus.closure_reason}
            onChange={(e) => setClosureStatus({ ...closureStatus, closure_reason: e.target.value })}
            placeholder="e.g., Private function, Staff training, Refurbishment"
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>

      <button
        onClick={saveStatus}
        disabled={saving}
        className="flex items-center gap-2 bg-[#C17C3A] text-white px-6 py-2 rounded-lg hover:bg-[#A0652D] transition-colors disabled:opacity-50"
      >
        {saving ? "Saving..." : saved ? <><Check size={18} /> Saved!</> : <><Save size={18} /> Save Closure Status</>}
      </button>
    </div>
  );
};

// About Section Manager Component
const AboutManager = ({ token }) => {
  const [about, setAbout] = useState({
    title: "",
    subtitle: "",
    content: ""
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchAbout = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/about`);
      const data = await res.json();
      setAbout({
        title: data.title || "",
        subtitle: data.subtitle || "",
        content: data.content || ""
      });
    } catch (err) {
      logError("Error fetching about:", err);
    }
  }, []);

  useEffect(() => {
    fetchAbout();
  }, [fetchAbout]);

  const saveAbout = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/about`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify(about),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      logError("Error saving about:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            type="text"
            value={about.title}
            onChange={(e) => setAbout({ ...about, title: e.target.value })}
            placeholder="e.g., A Warm Welsh Welcome"
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle (optional)</label>
          <input
            type="text"
            value={about.subtitle}
            onChange={(e) => setAbout({ ...about, subtitle: e.target.value })}
            placeholder="e.g., Traditional Welsh Hospitality"
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
          <textarea
            value={about.content}
            onChange={(e) => setAbout({ ...about, content: e.target.value })}
            placeholder="Write about your pub..."
            rows={6}
            className="w-full px-3 py-2 border rounded-lg resize-none"
          />
          <p className="text-xs text-gray-500 mt-1">This text appears in the About section on the homepage.</p>
        </div>
      </div>

      <button
        onClick={saveAbout}
        disabled={saving}
        className="flex items-center gap-2 bg-[#C17C3A] text-white px-6 py-2 rounded-lg hover:bg-[#A0652D] transition-colors disabled:opacity-50"
      >
        {saving ? "Saving..." : saved ? <><Check size={18} /> Saved!</> : <><Save size={18} /> Save About Section</>}
      </button>
    </div>
  );
};

// Updates Manager Component
const UpdatesManager = ({ token }) => {
  const [updates, setUpdates] = useState({
    title: "Latest Updates",
    content: "",
    show_section: false
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchUpdates = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/updates`);
      const data = await res.json();
      setUpdates({
        title: data.title || "Latest Updates",
        content: data.content || "",
        show_section: data.show_section || false
      });
    } catch (err) {
      logError("Error fetching updates:", err);
    }
  }, []);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  const saveUpdates = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/updates`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      logError("Error saving updates:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="space-y-4 mb-6">
        {/* Show/Hide Toggle */}
        <div className="flex items-center justify-between p-4 bg-[#F5F1E6] rounded-lg">
          <div>
            <p className="font-medium text-[#2C1810]">Show Updates Section</p>
            <p className="text-sm text-gray-600">Toggle to show or hide the updates banner on the website</p>
          </div>
          <button
            onClick={() => setUpdates({ ...updates, show_section: !updates.show_section })}
            className={`relative w-14 h-8 rounded-full transition-colors ${
              updates.show_section ? "bg-green-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                updates.show_section ? "translate-x-7" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            type="text"
            value={updates.title}
            onChange={(e) => setUpdates({ ...updates, title: e.target.value })}
            placeholder="e.g., Latest Updates, News, Announcements"
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
          <textarea
            value={updates.content}
            onChange={(e) => setUpdates({ ...updates, content: e.target.value })}
            placeholder="Write your update message here... e.g., 'We're now taking bookings for Christmas! Call us to reserve your table.'"
            rows={4}
            className="w-full px-3 py-2 border rounded-lg resize-none"
          />
          <p className="text-xs text-gray-500 mt-1">This will appear as a highlighted banner at the top of the page.</p>
        </div>
      </div>

      <button
        onClick={saveUpdates}
        disabled={saving}
        className="flex items-center gap-2 bg-[#C17C3A] text-white px-6 py-2 rounded-lg hover:bg-[#A0652D] transition-colors disabled:opacity-50"
      >
        {saving ? "Saving..." : saved ? <><Check size={18} /> Saved!</> : <><Save size={18} /> Save Updates</>}
      </button>
    </div>
  );
};

// Gallery Manager Component
const GalleryManager = ({ token }) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const fetchImages = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/gallery`);
      const data = await res.json();
      setImages(data.images || []);
    } catch (err) {
      logError("Error fetching gallery:", err);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      setError("Only JPG and PNG images are allowed");
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      setError("Image must be less than 20MB");
      return;
    }

    setError("");
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/gallery`, {
        method: "POST",
        headers: getAuthHeader(),
        body: formData,
      });

      if (res.ok) {
        fetchImages();
        e.target.value = ""; // Reset input
      } else {
        const data = await res.json();
        setError(data.detail || "Upload failed");
      }
    } catch (err) {
      logError("Error uploading image:", err);
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm("Delete this image from the gallery?")) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/gallery/${imageId}`, {
        method: "DELETE",
        headers: getAuthHeader(),
      });

      if (res.ok) {
        fetchImages();
      }
    } catch (err) {
      logError("Error deleting image:", err);
    }
  };

  return (
    <div>
      {/* Upload Section */}
      <div className="mb-6 p-4 bg-[#F5F1E6] rounded-lg border-2 border-dashed border-[#C17C3A]">
        <div className="text-center">
          <Upload className="mx-auto text-[#C17C3A] mb-2" size={32} />
          <p className="font-medium text-[#2C1810] mb-2">Upload Images</p>
          <p className="text-sm text-gray-600 mb-4">JPG or PNG, max 20MB</p>
          
          <label className="inline-flex items-center gap-2 bg-[#C17C3A] text-white px-6 py-2 rounded-lg hover:bg-[#A0652D] transition-colors cursor-pointer">
            <Plus size={18} />
            {uploading ? "Uploading..." : "Choose Image"}
            <input
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
          
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>
      </div>

      {/* Gallery Grid */}
      {images.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div key={image.id} className="relative group aspect-square">
              <img
                src={`${BACKEND_URL}${image.url}`}
                alt={image.original_name || "Gallery"}
                className="w-full h-full object-cover rounded-lg"
              />
              <button
                onClick={() => deleteImage(image.id)}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
              >
                <Trash2 size={16} />
              </button>
              <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate rounded-b-lg">
                {image.original_name}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-center py-8 italic">
          No images uploaded yet. The gallery section will be hidden on the website until you add images.
        </p>
      )}
    </div>
  );
};

// Blocked Dates Manager Component
const BlockedDatesManager = ({ token }) => {
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlock, setNewBlock] = useState({ date: "", time: "", reason: "" });
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchBlockedDates = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/blocked-dates`);
      const data = await res.json();
      setBlockedDates(data.blocked || []);
    } catch (err) {
      logError("Error fetching blocked dates:", err);
    }
  }, []);

  useEffect(() => {
    fetchBlockedDates();
  }, [fetchBlockedDates]);

  const addBlock = async () => {
    if (!newBlock.date) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/blocked-dates`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({
          date: newBlock.date,
          time: newBlock.time || null,
          reason: newBlock.reason
        }),
      });
      if (res.ok) {
        fetchBlockedDates();
        setNewBlock({ date: "", time: "", reason: "" });
        setShowAddForm(false);
      }
    } catch (err) {
      logError("Error adding block:", err);
    }
  };

  const removeBlock = async (blockId) => {
    if (!window.confirm("Remove this blocked date/time?")) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/blocked-dates/${blockId}`, {
        method: "DELETE",
        headers: getAuthHeader(),
      });
      if (res.ok) {
        fetchBlockedDates();
      }
    } catch (err) {
      logError("Error removing block:", err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (time24) => {
    if (!time24) return "All Day";
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Generate time slot options (same as booking page)
  const timeSlotOptions = [];
  // Lunch: 12:00 to 15:00
  for (let hour = 12; hour < 15; hour++) {
    for (let min = 0; min < 60; min += 15) {
      const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      timeSlotOptions.push(time);
    }
  }
  // Dinner: 18:00 to 21:00
  for (let hour = 18; hour < 21; hour++) {
    for (let min = 0; min < 60; min += 15) {
      const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      timeSlotOptions.push(time);
    }
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Block specific dates or time slots to prevent new bookings. Leave time empty to block the entire day.
      </p>

      {/* Add Block Form */}
      {showAddForm ? (
        <div className="bg-[#F5F1E6] rounded-lg p-4">
          <h3 className="font-semibold text-[#2C1810] mb-4">Add Blocked Date/Time</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={newBlock.date}
                onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time (optional)</label>
              <select
                value={newBlock.time}
                onChange={(e) => setNewBlock({ ...newBlock, time: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">Block Entire Day</option>
                {timeSlotOptions.map(time => (
                  <option key={time} value={time}>{formatTime(time)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <input
                type="text"
                value={newBlock.reason}
                onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
                placeholder="e.g., Private function"
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={addBlock}
                disabled={!newBlock.date}
                className="flex-1 bg-[#C17C3A] text-white px-4 py-2 rounded-lg hover:bg-[#A0652D] transition-colors disabled:opacity-50"
              >
                Add Block
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewBlock({ date: "", time: "", reason: "" });
                }}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 text-[#C17C3A] hover:text-[#A0652D] font-medium"
        >
          <Plus size={20} />
          Add Blocked Date/Time
        </button>
      )}

      {/* Blocked Dates List */}
      {blockedDates.length > 0 ? (
        <div className="space-y-3">
          <h3 className="font-semibold text-[#2C1810]">Currently Blocked ({blockedDates.length})</h3>
          {blockedDates
            .sort((a, b) => a.date.localeCompare(b.date) || (a.time || "").localeCompare(b.time || ""))
            .map((block) => (
              <div key={block.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div>
                  <span className="font-medium">{formatDate(block.date)}</span>
                  <span className="mx-2 text-gray-400">|</span>
                  <span className={block.time ? "text-[#C17C3A]" : "text-red-600 font-medium"}>
                    {formatTime(block.time)}
                  </span>
                  {block.reason && (
                    <span className="ml-2 text-sm text-gray-500">- {block.reason}</span>
                  )}
                </div>
                <button
                  onClick={() => removeBlock(block.id)}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
        </div>
      ) : (
        <p className="text-gray-500 italic">No blocked dates or times.</p>
      )}
    </div>
  );
};

// Main Admin Page Component
export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const navigate = useNavigate();

  // Validate token on mount - ensures stored tokens are still valid
  useEffect(() => {
    const validateStoredToken = async () => {
      const savedToken = localStorage.getItem("admin_token");
      if (!savedToken) {
        setInitializing(false);
        return;
      }

      try {
        // Validate the token with a simple authenticated request
        const res = await fetch(`${BACKEND_URL}/api/admin/validate-token`, {
          headers: { Authorization: `Bearer ${savedToken}` },
        });

        if (res.ok) {
          setToken(savedToken);
          setIsLoggedIn(true);
        } else {
          // Token is invalid or expired - clear it
          localStorage.removeItem("admin_token");
        }
      } catch (err) {
        // On network error, still try to use the token (offline mode)
        // But log the error in development
        if (isDev) {
          logError("Token validation failed:", err);
        }
        setToken(savedToken);
        setIsLoggedIn(true);
      } finally {
        setInitializing(false);
      }
    };

    validateStoredToken();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem("admin_token", data.token);
        setToken(data.token);
        setIsLoggedIn(true);
      } else {
        setError("Invalid username or password");
      }
    } catch (err) {
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setIsLoggedIn(false);
  };

  // Show loading spinner while validating stored token
  if (initializing) {
    return (
      <div className="min-h-screen bg-[#F5F1E6] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#C17C3A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#F5F1E6] flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="font-playfair text-3xl font-bold text-[#2C1810]">Admin Login</h1>
            <p className="font-manrope text-gray-600 mt-2">The Three Horseshoes</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C17C3A] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C17C3A] focus:border-transparent"
                required
              />
            </div>

            {error && (
              <p className="text-red-600 text-sm">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C17C3A] text-white py-3 rounded-lg font-semibold hover:bg-[#A0652D] transition-colors disabled:opacity-50"
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>

          <button
            onClick={() => navigate("/")}
            className="w-full mt-4 text-[#2C1810] py-2 text-sm hover:underline"
          >
            ← Back to Website
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1E6]">
      {/* Header */}
      <header className="bg-[#2C1810] text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-playfair text-2xl font-bold">Admin Dashboard</h1>
            <p className="font-manrope text-[#C17C3A] text-sm">The Three Horseshoes</p>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="text-sm text-[#F5F1E6]/80 hover:text-white transition-colors"
            >
              View Website →
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <CollapsibleSection title="Updates / Announcements" icon={Bell} defaultOpen={false}>
          <UpdatesManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="About Section" icon={FileText} defaultOpen={false}>
          <AboutManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Photo Gallery" icon={Image} defaultOpen={false}>
          <GalleryManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Menu Items" icon={Utensils} defaultOpen={true}>
          <MenuManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Drinks" icon={Beer}>
          <DrinksManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="What's On (Weekly Events)" icon={Calendar}>
          <EventsManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Contact Information" icon={Phone}>
          <ContactManager token={token} />
        </CollapsibleSection>

        <CollapsibleSection title="Pub & Kitchen Status" icon={AlertCircle}>
          <ClosureManager token={token} />
        </CollapsibleSection>
      </main>
    </div>
  );
}
